/** Shared pieces of the customer view: one plain card style, phone first. */
import React from "react";
import PageTitle from "@/components/layout/PageTitle";

export const STAGE_TONE = {
  waiting: "bg-[#FEF6E7] text-[#865503]",
  on_the_way: "bg-[#E3EFFC] text-[#04326B]",
  at_port: "bg-[#E3EFFC] text-[#04326B]",
  on_the_water: "bg-[#E3EFFC] text-[#04326B]",
  arriving: "bg-[#E7F6EC] text-[#036B26]",
  delivered: "bg-[#F0F2F5] text-[#344054]",
};

export function Page({ title, children, actions = null }) {
  return (
    <div className="h-full flex-1 overflow-auto pb-24 lg:pb-10">
      {/* On a phone the app header already names the page. */}
      <PageTitle className="hidden lg:flex" actions={actions}>
        {title}
      </PageTitle>
      <div className="mx-auto w-full max-w-[720px] px-4 pb-6 pt-4 lg:px-8 lg:pt-0">{children}</div>
    </div>
  );
}

export function Card({ children, className = "", ...props }) {
  return (
    <div className={`rounded-2xl border border-[#E4E7EC] bg-white p-4 ${className}`} {...props}>
      {children}
    </div>
  );
}

export function Pill({ tone = "bg-[#F0F2F5] text-[#344054]", children }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[12px] font-medium ${tone}`}>
      {children}
    </span>
  );
}

export function Empty({ title, children }) {
  return (
    <Card className="py-10 text-center">
      <p className="text-[15px] font-medium text-[#1D2739]">{title}</p>
      {children ? <p className="mt-1 text-[14px] text-[#667185]">{children}</p> : null}
    </Card>
  );
}

export function Loading() {
  return (
    <div className="space-y-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="h-24 animate-pulse rounded-2xl bg-[#F0F2F5]" />
      ))}
    </div>
  );
}

export function day(iso) {
  if (!iso) return null;
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

const AVATAR_TONES = ["bg-[#DBEAFE] text-[#1D4ED8]", "bg-[#E7F6EC] text-[#036B26]", "bg-[#FEF6E7] text-[#865503]",
  "bg-[#F3E8FF] text-[#6B21A8]", "bg-[#FBEAE9] text-[#9E0A05]", "bg-[#F0F2F5] text-[#344054]"];

/** Initials in a soft circle, the same colour for the same name everywhere. */
export function Avatar({ name = "?", size = 36 }) {
  const initials = name.replace(/[^\p{L}\s]/gu, "").split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0].toUpperCase()).join("") || "?";
  const tone = AVATAR_TONES[[...name].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_TONES.length];
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full font-semibold ${tone}`}
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials}
    </span>
  );
}

export const ROLE_LABEL = { owner: "Owner", approver: "Approver", member: "Member", partner: "Partner" };
export const ROLE_TONE = {
  owner: "bg-[#EFF6FF] text-[#1D4ED8]",
  approver: "bg-[#E7F6EC] text-[#036B26]",
  member: "bg-[#F0F2F5] text-[#344054]",
  partner: "bg-[#FEF6E7] text-[#865503]",
};

/** "Where we left off" on a car: who last asked what, what it waits on, what's next. */
export function LeftOff({ line, compact = false }) {
  if (!line) return null;
  if (compact) {
    return (
      <p className="mt-2 line-clamp-1 text-[13px] text-[#475367]">
        {line.who ? (
          <>
            <span className="font-medium text-[#1D2739]">{line.who}</span> · {line.when}: {line.said}
          </>
        ) : (
          <>Waiting on {line.waiting_on}</>
        )}
      </p>
    );
  }
  return (
    <Card className="border-[#BFDBFE] bg-[#F8FBFF]">
      <p className="text-[12px] font-semibold uppercase tracking-wide text-[#2563EB]">Where we left off</p>
      {line.who ? (
        <div className="mt-3 flex gap-3">
          <Avatar name={line.who} size={32} />
          <div className="min-w-0">
            <p className="text-[14px] text-[#101928]">
              <span className="font-semibold">{line.who}</span>{" "}
              <span className="text-[#667185]">
                · {line.when} · {line.where}
              </span>
            </p>
            <p className="mt-1 text-[14px] leading-snug text-[#344054]">“{line.said}”</p>
          </div>
        </div>
      ) : null}
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="rounded-xl bg-white px-3 py-2">
          <p className="text-[12px] text-[#667185]">Waiting on</p>
          <p className="text-[14px] font-medium text-[#1D2739]">{line.waiting_on}</p>
        </div>
        <div className="rounded-xl bg-white px-3 py-2">
          <p className="text-[12px] text-[#667185]">Next</p>
          <p className="text-[14px] font-medium text-[#1D2739]">{line.next ?? "—"}</p>
        </div>
      </div>
    </Card>
  );
}
